import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, ArrowLeft } from "@phosphor-icons/react";

const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "February 2, 2026";

  return (
    <div data-testid="privacy-policy-page">
      {/* Hero Section */}
      <section className="py-16 bg-hero-gradient relative overflow-hidden">
        <div className="decorative-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
        
        <div className="max-w-4xl mx-auto container-padding relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors"
            >
              <ArrowLeft weight="bold" className="w-4 h-4" />
              Back to Home
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Shield weight="duotone" className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h1 className="font-display text-4xl md:text-5xl text-foreground">
                  Privacy Policy
                </h1>
                <p className="text-muted-foreground">Last Updated: {lastUpdated}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto container-padding">
          <motion.div
            className="prose prose-lg max-w-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl border border-border p-8 md:p-12 space-y-8">
              
              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">1. Introduction</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Noble Hospice Solutions ("we," "our," or "us") is committed to protecting your privacy. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                  when you visit our website noblehealthcareinitiatives.com and use our consulting services.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  We are fully compliant with the Health Insurance Portability and Accountability Act (HIPAA) 
                  and maintain the highest standards for protecting healthcare information.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">2. Information We Collect</h2>
                <h3 className="font-semibold text-foreground text-lg mt-6 mb-3">Personal Information</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We may collect personal information that you voluntarily provide when you:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-3 space-y-2">
                  <li>Fill out contact forms or consultation requests</li>
                  <li>Subscribe to our newsletter</li>
                  <li>Download resources from our website</li>
                  <li>Communicate with us via email or phone</li>
                  <li>Engage with our AI chat assistant</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  This information may include your name, email address, phone number, organization name, 
                  and any other information you choose to provide.
                </p>

                <h3 className="font-semibold text-foreground text-lg mt-6 mb-3">Automatically Collected Information</h3>
                <p className="text-muted-foreground leading-relaxed">
                  When you visit our website, we automatically collect certain information including:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-3 space-y-2">
                  <li>Device and browser information</li>
                  <li>IP address and general location data</li>
                  <li>Pages visited and time spent on pages</li>
                  <li>Referring website information</li>
                </ul>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">3. How We Use Your Information</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-3 space-y-2">
                  <li>Respond to your inquiries and provide consulting services</li>
                  <li>Send you requested resources and newsletters</li>
                  <li>Schedule and conduct consultations</li>
                  <li>Improve our website and services</li>
                  <li>Comply with legal obligations</li>
                  <li>Protect against fraudulent or illegal activity</li>
                </ul>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">4. HIPAA Compliance</h2>
                <p className="text-muted-foreground leading-relaxed">
                  As a healthcare consulting company, we understand the importance of protecting sensitive 
                  health information. We maintain administrative, technical, and physical safeguards to 
                  protect any Protected Health Information (PHI) that may be shared with us during the 
                  course of our consulting engagements.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  We will enter into Business Associate Agreements (BAAs) with clients as required by HIPAA 
                  and will only use or disclose PHI as permitted by law and our agreements with you.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">5. Data Security</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your personal 
                  information against unauthorized access, alteration, disclosure, or destruction. These 
                  measures include:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-3 space-y-2">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Secure access controls and authentication</li>
                  <li>Regular security assessments and updates</li>
                  <li>Employee training on data protection</li>
                </ul>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">6. Information Sharing</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We do not sell, trade, or rent your personal information to third parties. We may share 
                  your information only in the following circumstances:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-3 space-y-2">
                  <li>With your consent</li>
                  <li>To comply with legal obligations</li>
                  <li>With service providers who assist in our operations (under strict confidentiality agreements)</li>
                  <li>To protect our rights, privacy, safety, or property</li>
                </ul>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">7. Your Rights</h2>
                <p className="text-muted-foreground leading-relaxed">
                  You have the right to:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-3 space-y-2">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your personal information</li>
                  <li>Opt out of marketing communications</li>
                  <li>Withdraw consent where processing is based on consent</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  To exercise these rights, please contact us using the information provided below.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">8. Cookies and Tracking</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our website uses cookies and similar tracking technologies to enhance your experience. 
                  You can control cookie settings through your browser preferences. Note that disabling 
                  cookies may affect some website functionality.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">9. Third-Party Links</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our website may contain links to third-party websites. We are not responsible for the 
                  privacy practices of these external sites. We encourage you to review their privacy 
                  policies before providing any personal information.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">10. Changes to This Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Privacy Policy from time to time. The updated version will be 
                  indicated by the "Last Updated" date at the top of this page. We encourage you to 
                  review this policy periodically for any changes.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">11. Contact Us</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have any questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="mt-4 p-6 bg-muted/50 rounded-xl">
                  <p className="font-semibold text-foreground">Noble Hospice Solutions</p>
                  <p className="text-muted-foreground mt-2">
                    Email: <a href="mailto:noblehospicesolutions@gmail.com" className="text-primary hover:underline">noblehospicesolutions@gmail.com</a>
                  </p>
                  <p className="text-muted-foreground">
                    Phone: <a href="tel:+17183046214" className="text-primary hover:underline">+1 (718) 304-6214</a>
                  </p>
                  <p className="text-muted-foreground">
                    Hours: Mon - Fri: 8AM - 6PM PST | Sat - Sun: 8AM - 6PM EST
                  </p>
                </div>
              </section>

            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
